// frontend/src/components/ui/SectionCard.tsx
// Section card primitive matching proto `.panel` / `.card-h` / `.scard` look.
// Presentational only — no data fetching, no mutations.

import type { ReactNode } from "react";
import { cn } from "../../lib/cn";

export interface SectionCardProps {
  title: string;
  /** Grey subtitle text shown inline after the title */
  hint?: string;
  /** Right side of the header row (e.g. an edit button) */
  action?: ReactNode;
  /** Danger zone variant: red border + red-soft background */
  danger?: boolean;
  children: ReactNode;
  className?: string;
  /** Override body padding */
  bodyClassName?: string;
}

export function SectionCard({
  title,
  hint,
  action,
  danger = false,
  children,
  className,
  bodyClassName,
}: SectionCardProps): JSX.Element {
  return (
    <section
      className={cn(
        // Proto .panel / .scard base
        "bg-surface border border-border rounded-[11px] shadow-card overflow-hidden",
        danger && "border-[#fbcdcd] bg-red-soft/50",
        className,
      )}
    >
      {/* Proto .card-h header */}
      <div
        className={cn(
          "flex items-center gap-2 px-[15px] py-[12px]",
          "border-b",
          danger ? "border-[#fbcdcd]" : "border-border-2",
        )}
      >
        {/* Proto h3: font-display, semibold, tight tracking */}
        <h3
          className={cn(
            "font-display font-semibold text-[15px] tracking-[-0.01em] text-text",
          )}
        >
          {title}
        </h3>
        {hint && (
          <span className="font-sans text-[12px] text-muted font-normal ml-1">
            {hint}
          </span>
        )}
        {action && <div className="ml-auto shrink-0">{action}</div>}
      </div>

      {/* Card body */}
      <div className={cn("px-[15px] py-[9px]", bodyClassName)}>{children}</div>
    </section>
  );
}
